/** @format */

import { element } from 'protractor';
/** @format */
import { Injectable } from '@angular/core';
import { Observable, combineLatest, defer, of, pipe } from 'rxjs';
import { map, tap, take, switchMap, first } from 'rxjs/operators';
import { AngularFireDatabase } from '@angular/fire/database';
import {
  AngularFirestore,
  AngularFirestoreCollection,
  AngularFirestoreDocument,
  QuerySnapshot,
  DocumentChangeAction,
  DocumentSnapshotDoesNotExist,
  DocumentSnapshotExists,
  Action,
} from '@angular/fire/firestore';
import firebase from 'firebase/app';

export const docJoin = (afs: AngularFirestore, field: string, collection: string) => {
  return source =>
    defer(() => {
      let parent;
      const keys = Object.keys(field);

      return source.pipe(
        switchMap(data => {
          // Save the parent data state
          parent = data;
          if (data) {
            const arr = data[field];
            const reads$ = [];

            for (const id of arr) {
              // Map each path to an Observable
              const docs$ = afs
                .doc(`${collection}/${id}`)
                .snapshotChanges()
                .pipe(
                  map(doc => {
                    const data: any = doc.payload.data();
                    const id = doc.payload.id;
                    return { id: doc.payload.id, ...data };
                  })
                );
              reads$.push(docs$);
            }
            // return combineLatest, it waits for all reads to finish
            return combineLatest(reads$);
          } else {
            return of([]);
          }
        }),
        map(arr => {
          // Return the parent doc with the joined objects
          return { ...parent, [field]: arr };
        })
      );
    });
};

export const listJoin = (afs: AngularFirestore, field: string, collection: string) => {
  return source =>
    defer(() => {
      let parent;
      const keys = Object.keys(field);
      return source.pipe(
        switchMap(async data => {
          // Save the parent data state
          parent = data;
          if (!parent || typeof parent == 'undefined') {
            return of([]);
          }
          let result = [];
          if (parent) {
            for (const doc of parent) {
              const reads$ = [];
              const arr = doc[field];
              for (const id of arr) {
                const docs$ = await afs
                  .doc(`${collection}/${id}`)
                  .snapshotChanges()
                  .pipe(
                    map((doc: any) => {
                      const data: any = doc.payload.data();
                      const id = doc.payload.id;
                      return { id: doc.payload.id, ...data };
                    })
                  )
                  .pipe(take(1))
                  .toPromise();

                reads$.push(docs$);
              }
              reads$.length ? reads$ : [];
              result.push({ ...doc, detailList: reads$ });
            }
          }

          return result.length ? result : [];
        }),
        map((data: any) => {
          return data;
        })
      );
    });
};
export const leftJoinDocument = (afs: AngularFirestore, field, collection) => {
  return source =>
    defer(() => {
      // Operator state
      let collectionData;
      let real: any;
      const cache = new Map();
      return source.pipe(
        switchMap(data => {
          real = data;
          // Clear mapping on each emitted val ;
          cache.clear();

          // Save the parent data state
          collectionData = data as any[];

          const reads$ = [];
          let i = 0;
          for (const doc of collectionData) {
            // Skip if doc field does not exist or is already in cache
            if (!doc[field] || cache.get(doc[field])) {
              continue;
            }

            // Push doc read to Array
            reads$.push(afs.collection(collection).doc(doc[field]).valueChanges());
            cache.set(doc[field], i);
            i++;
          }

          return reads$.length ? combineLatest(reads$) : of([]);
        }),
        map(joins => {
          return collectionData.map((v, i) => {
            const joinIdx = cache.get(v[field]);
            const item = real.find(e => e.id == v[field]);
            if (field && v[field]) {
              return {
                ...v,
                [field]: { ...joins[joinIdx], id: v[field] } || null,
              };
            } else {
              return v;
            }
          });
        })
      );
    });
};

export const docListJoin = (afs: AngularFirestore, field: string, collection: string) => {
  return source =>
    defer(() => {
      let parent: any;
      let form;

      const cache = new Map();
      return source.pipe(
        switchMap(async data => {
          // Save the parent data state
          parent = data;
          if (!parent || typeof parent == 'undefined') {
            return of([]);
          }
          let result = [];
          if (parent) {
            const reads$ = [];
            const arr = data[field];
            for (const id of arr) {
              const docs$ = await afs
                .doc(`${collection}/${id}`)
                .snapshotChanges()
                .pipe(
                  map((doc: any) => {
                    const data: any = doc.payload.data();
                    return data;
                  })
                )
                .pipe(take(1))
                .toPromise();

              reads$.push(docs$);
            }
            reads$.length ? reads$ : [];
            result.push({ ...parent, [field]: reads$ });
          }

          return result.length ? result : [];
        }),
        map((data: any) => {
          return data[0];
        })
      );
    });
};
type CollectionPredicate<T> = string | AngularFirestoreCollection<T>;
type DocPredicate<T> = string | AngularFirestoreDocument<T>;

@Injectable({
  providedIn: 'root',
})
export class DbService {
  public user;
  constructor(public afs: AngularFirestore) {}

  createdAt() {
    return firebase.firestore.FieldValue.serverTimestamp();
  }

  collection$(path, query?) {
    return this.afs
      .collection(path, query)
      .snapshotChanges()
      .pipe(
        map(actions => {
          return actions.map(a => {
            const data: any = a.payload.doc.data();
            const id = a.payload.doc.id;
            return { id, ...data };
          });
        })
      );
  }

  doc$(path): Observable<any> {
    return this.afs
      .doc(path)
      .snapshotChanges()
      .pipe(
        map(doc => {
          const data: any = doc.payload.data();
          const id = doc.payload.id;
          return { id: doc.payload.id, ...data };
        })
      );
  }

  doc2$<T>(ref: DocPredicate<T>): Observable<T> {
    return this.doc(ref)
      .snapshotChanges()
      .pipe(
        map((doc: Action<DocumentSnapshotDoesNotExist | DocumentSnapshotExists<T>>) => {
          return doc.payload.data() as T;
        })
      );
  }

  col$<T>(ref: CollectionPredicate<T>, queryFn?): Observable<T[]> {
    return this.col(ref, queryFn)
      .snapshotChanges()
      .pipe(
        map((docs: DocumentChangeAction<T>[]) => {
          return docs.map((a: DocumentChangeAction<T>) => a.payload.doc.data()) as T[];
        })
      );
  }

  col<T>(ref: CollectionPredicate<T>, queryFn?): AngularFirestoreCollection<T> {
    return typeof ref === 'string' ? this.afs.collection<T>(ref, queryFn) : ref;
  }

  doc<T>(ref: DocPredicate<T>): AngularFirestoreDocument<T> {
    return typeof ref === 'string' ? this.afs.doc<T>(ref) : ref;
  }

  /**
   * @param  {string} path 'collection' or 'collection/docID'
   * @param  {object} data new data
   *
   * Creates or updates data on a collection or document.
   **/

  // ** 기본적인 DB처리 **//
  updateAt(path: string, data: Object): Promise<any> {
    const segments = path.split('/').filter(v => v);
    if (segments.length % 2) {
      // Odd is always a collection
      return this.afs.collection(path).add(data);
    } else {
      // Even is always document
      return this.afs.doc(path).set(data, { merge: true });
    }
  }

  /**
   * @param  {string} path path to document
   *
   * Deletes document from Firestore
   **/

  delete(path) {
    return this.afs.doc(path).delete();
  }

  // Helper to format the docId for relationships
  public concatIds(a: string, b: string) {
    return `${a}_${b}`;
  }

  createId() {
    return this.afs.createId();
  }
}
